import type { OrderGetResponseType } from "@sellar-io/api-v2/types";
import { calculateItemTotal, formatCurrency } from "@sellar-io/common";
import { calculateUnifiedCheckoutOrderPricing } from "../lib/calculateUnifiedCheckoutOrderPricing";
import { useMemo } from "react";
import { mapBasketOrderToCheckoutOrderItems } from "../../stockList/checkoutV2/lib/mapBasketOrderToCheckoutOrderItems";
import { useUnifiedCheckout } from "../UnifiedCheckoutProvider.shared";
import { usePaidOrders } from "./usePaidOrders";

export const useUnifiedCheckoutSummaryItems = () => {
  const { sdBasket, sdOrders, supplierInfoMap } = useUnifiedCheckout();
  const createdOrderQueries = usePaidOrders([
    ...new Set([
      ...(sdBasket.paidOrderIds ?? []),
      ...(sdBasket.pendingOrderIds ?? []),
      ...(sdBasket.failedOrderIds ?? []),
    ]),
  ]);

  const createdOrders = useMemo(
    () =>
      createdOrderQueries
        .map((query) => query.data)
        .filter((order): order is OrderGetResponseType => order != null),
    [createdOrderQueries],
  );

  const supplierOrder = useMemo(
    () =>
      [
        ...new Set([
          ...sdOrders.map((order) => order.SupplierId),
          ...createdOrders.map((order) => order.SupplierId),
        ]),
      ]
        .filter((supplierId): supplierId is number => supplierId != null)
        .sort((first, second) => first - second),
    [createdOrders, sdOrders],
  );

  const orderItemList = useMemo(() => {
    return supplierOrder.flatMap((supplierId) => {
      const draftOrder = sdOrders.find(
        (order) => order.SupplierId === supplierId,
      );
      if (draftOrder) {
        return mapBasketOrderToCheckoutOrderItems(
          draftOrder.OrderItems ?? [],
          supplierInfoMap.get(supplierId)?.supplier.Business.name,
        );
      }

      return createdOrders
        .filter((order) => order.SupplierId === supplierId)
        .flatMap((order) =>
          mapBasketOrderToCheckoutOrderItems(
            order.OrderItems,
            order.Supplier.Business.name,
          ).map((item) => ({ ...item, isCompleted: true })),
        );
    });
  }, [createdOrders, sdOrders, supplierInfoMap, supplierOrder]);

  const orderSummary = useMemo(() => {
    const draftPricing = sdOrders.map((order) =>
      calculateUnifiedCheckoutOrderPricing({
        orderItems: order.OrderItems ?? [],
        storefront: supplierInfoMap.get(order.SupplierId ?? 0)?.storefront,
        vatPercentage: order.vatPercentage ?? 0,
      }),
    );
    const subtotal = calculateItemTotal(orderItemList);
    const discount =
      draftPricing.reduce((total, pricing) => total + pricing.discount, 0) +
      createdOrders.reduce(
        (total, order) => total + (order.discountAmount ?? 0),
        0,
      );
    const total =
      draftPricing.reduce((sum, pricing) => sum + pricing.total, 0) +
      createdOrders.reduce((sum, order) => sum + order.price, 0);
    const currency =
      sdOrders[0]?.currency ?? createdOrders[0]?.currency ?? "gbp";

    return {
      deliveryCharge: 0,
      isDeliveryChargeCapped: false,
      subTotal: formatCurrency(subtotal, currency),
      discount:
        discount > 0
          ? {
              calculatedTotal: formatCurrency(discount, currency),
              value: formatCurrency(discount, currency),
            }
          : undefined,
      tax: {
        name: "VAT",
        percentage: "",
        total: formatCurrency(total - (subtotal - discount), currency),
      },
      total: formatCurrency(total, currency),
      totalCharge: total,
    };
  }, [createdOrders, orderItemList, sdOrders, supplierInfoMap]);

  return {
    orderSummary,
    createdOrders,
    isLoading: createdOrderQueries.some((query) => query.isPending),
    orderItemList,
  };
};
